/**
 * In-app report for offensive AI-generated content (a chat answer or a
 * generated image). Mirrors the mobile app's ReportContentDialog: the AI
 * content is always sent; the user's request and the image are opt-out so
 * the user decides what of their own goes to support.
 */

import { useEffect, useState } from 'preact/hooks'
import { reportContent, type ReportReason } from '../services/api'
import type { CloudNote } from '../types'

// ── Report flag visibility (Settings › About) ────────────────────
// On by default. Only the flag icons on chat replies follow it — the
// "Report content" menu item and the labelled Report buttons stay, so
// reporting is always reachable in-app (Google Play AI content policy).

const FLAGS_VISIBLE_KEY = 'pipar_report_flags_visible'
const FLAGS_CHANGED_EVENT = 'pipar-report-flags-changed'

export function getReportFlagsVisible(): boolean {
  try {
    return localStorage.getItem(FLAGS_VISIBLE_KEY) !== 'false'
  } catch {
    return true
  }
}

export function setReportFlagsVisible(visible: boolean): void {
  try {
    localStorage.setItem(FLAGS_VISIBLE_KEY, visible ? 'true' : 'false')
  } catch {
    // Storage blocked: the change still applies for this page load.
  }
  window.dispatchEvent(new CustomEvent(FLAGS_CHANGED_EVENT, { detail: visible }))
}

/** Current flag visibility; re-renders when it changes anywhere in the app
 *  (or in another tab). */
export function useReportFlagsVisible(): boolean {
  const [visible, setVisible] = useState(getReportFlagsVisible)
  useEffect(() => {
    const onChange = () => setVisible(getReportFlagsVisible())
    window.addEventListener(FLAGS_CHANGED_EVENT, onChange)
    window.addEventListener('storage', onChange)
    return () => {
      window.removeEventListener(FLAGS_CHANGED_EVENT, onChange)
      window.removeEventListener('storage', onChange)
    }
  }, [])
  return visible
}

export type ReportKind = 'image' | 'text' | 'document' | 'note'

/** Made by the AI rather than typed by the user: a generated document or
 *  image, or a note a persona created through a tool (local tool notes carry
 *  the persona's id as owner, cloud tool notes as persona_id; the user's own
 *  notes have neither, or 'user'). */
export function isAiCreatedNote(note: CloudNote): boolean {
  if (note.is_ai_generated_document || note.is_ai_generated_image) return true
  const candidates = [note.metadata?.owner, note.owner, note.metadata?.persona_id, note.persona_id]
  return candidates.some((v) => typeof v === 'string' && v !== '' && v !== 'user')
}

const TITLES: Record<ReportKind, string> = {
  image: 'Report generated image',
  text: 'Report AI response',
  document: 'Report generated document',
  note: 'Report AI-created note',
}

const CONTENT_LABELS: Record<ReportKind, string> = {
  image: 'Image prompt',
  text: 'AI response',
  document: 'Document content',
  note: 'Note content',
}

const REASONS: { id: ReportReason; label: string }[] = [
  { id: 'sexual', label: 'Sexual' },
  { id: 'violent', label: 'Violent' },
  { id: 'hateful', label: 'Hateful or harassing' },
  { id: 'self_harm', label: 'Self-harm' },
  { id: 'dangerous', label: 'Dangerous or illegal' },
  { id: 'misinformation', label: 'False or misleading' },
  { id: 'other', label: 'Other' },
]

/** Raw image bytes above this are not attached (mail host's 10 MB body cap). */
const MAX_IMAGE_BYTES = 7 * 1024 * 1024

interface ReportContentDialogProps {
  kind: ReportKind
  /** AI response text, or the image prompt. */
  aiContent: string
  userRequest?: string | null
  /** Displayable URL (blob: or data:) of the generated image. */
  imageUrl?: string | null
  messageId?: string
  onClose: (sent: boolean) => void
}

async function urlToBase64(url: string): Promise<{ base64: string; mimeType: string; size: number } | null> {
  try {
    const blob = await (await fetch(url)).blob()
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(blob)
    })
    return {
      base64: dataUrl.slice(dataUrl.indexOf(',') + 1),
      mimeType: blob.type && blob.type.startsWith('image/') ? blob.type : 'image/png',
      size: blob.size,
    }
  } catch (e) {
    console.warn('Could not read image for report:', e)
    return null
  }
}

export function ReportContentDialog({
  kind,
  aiContent,
  userRequest,
  imageUrl,
  messageId,
  onClose,
}: ReportContentDialogProps) {
  const hasUserRequest = !!userRequest?.trim()
  const hasImage = kind === 'image' && !!imageUrl
  const [reason, setReason] = useState<ReportReason>('other')
  const [comment, setComment] = useState('')
  const [includeUserRequest, setIncludeUserRequest] = useState(hasUserRequest)
  const [includeImage, setIncludeImage] = useState(hasImage)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSend = async () => {
    setSending(true)
    setError(null)
    let image: { base64: string; mimeType: string } | undefined
    if (includeImage && imageUrl) {
      const img = await urlToBase64(imageUrl)
      if (img && img.size <= MAX_IMAGE_BYTES) image = img
    }
    const result = await reportContent({
      contentType: kind === 'image' ? 'image' : 'text',
      source: kind === 'document' || kind === 'note' ? kind : undefined,
      reason,
      aiContent,
      userRequest: includeUserRequest && userRequest ? userRequest : undefined,
      comment,
      imageBase64: image?.base64,
      imageMimeType: image?.mimeType,
      messageId,
    })
    if (result === null) {
      onClose(true)
      return
    }
    setSending(false)
    setError(
      result === 'auth'
        ? 'Please sign in to send a report.'
        : result === 'rate_limited'
          ? 'Too many reports. Please try again later.'
          : 'Could not send the report. Please try again.',
    )
  }

  return (
    <div style={overlayStyle} onClick={() => !sending && onClose(false)}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
          {'\u{1F6A9}'} {TITLES[kind]}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          Let us know if this AI-generated content is offensive or inappropriate. The report is sent to PiPar support.
        </div>

        {hasImage && (
          <div style={{ textAlign: 'center', marginBottom: 10 }}>
            <img src={imageUrl!} alt="Reported image" style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, objectFit: 'contain' }} />
          </div>
        )}

        <div style={labelStyle}>{CONTENT_LABELS[kind]}</div>
        <div style={quoteStyle}>{aiContent.trim() || '-'}</div>

        <div style={labelStyle}>Reason</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {REASONS.map((r) => (
            <button
              key={r.id}
              type="button"
              disabled={sending}
              onClick={() => setReason(r.id)}
              style={{
                ...chipStyle,
                background: reason === r.id ? 'var(--primary)' : 'transparent',
                color: reason === r.id ? '#fff' : 'var(--text-secondary)',
              }}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div style={labelStyle}>Additional details (optional)</div>
        <textarea
          value={comment}
          disabled={sending}
          maxLength={2000}
          rows={3}
          placeholder="What was wrong with this content?"
          onInput={(e) => setComment((e.target as HTMLTextAreaElement).value)}
          style={textareaStyle}
        />

        {hasImage && (
          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={includeImage}
              disabled={sending}
              onChange={(e) => setIncludeImage((e.target as HTMLInputElement).checked)}
            />
            Include the image
          </label>
        )}

        {hasUserRequest && (
          <>
            <label style={checkboxRowStyle}>
              <input
                type="checkbox"
                checked={includeUserRequest}
                disabled={sending}
                onChange={(e) => setIncludeUserRequest((e.target as HTMLInputElement).checked)}
              />
              Include my request
            </label>
            {includeUserRequest && <div style={{ ...quoteStyle, maxHeight: 100 }}>{userRequest}</div>}
          </>
        )}

        {error && <div style={{ color: 'var(--danger)', fontSize: 13, marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button class="btn" type="button" disabled={sending} onClick={() => onClose(false)}>
            Cancel
          </button>
          <button class="btn btn-primary" type="button" disabled={sending} onClick={handleSend}>
            {sending ? 'Sending...' : 'Send report'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Styles ──────────────────────────────────────────────────────

const overlayStyle: Record<string, string | number> = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100, // above GeneratedContentPreview (1000)
  padding: 16,
}

const modalStyle: Record<string, string | number> = {
  width: '100%',
  maxWidth: 520,
  maxHeight: '90vh',
  overflowY: 'auto',
  background: 'var(--bg)',
  color: 'var(--text)',
  borderRadius: 12,
  border: '1px solid var(--border)',
  padding: 20,
  boxSizing: 'border-box',
}

const labelStyle: Record<string, string | number> = {
  fontSize: 13,
  fontWeight: 600,
  marginTop: 12,
  marginBottom: 6,
}

const quoteStyle: Record<string, string | number> = {
  fontSize: 12,
  lineHeight: '1.5',
  padding: 10,
  borderRadius: 8,
  background: 'var(--bg-surface)',
  color: 'var(--text-secondary)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 160,
  overflowY: 'auto',
}

const chipStyle: Record<string, string | number> = {
  padding: '4px 12px',
  borderRadius: 16,
  border: '1px solid var(--border)',
  fontSize: 12,
  cursor: 'pointer',
}

const textareaStyle: Record<string, string | number> = {
  width: '100%',
  boxSizing: 'border-box',
  padding: 8,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  fontSize: 13,
  fontFamily: 'inherit',
  resize: 'vertical',
}

const checkboxRowStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  marginTop: 12,
  cursor: 'pointer',
}
